import { getCognitoGroups, getCognitoIdToken } from "@src/appV2/Auth/cognito/cognitoAuth";
import { logError, logEvent } from "@src/appV2/lib/analytics/log";
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  COGNITO_IMPERSONATION_GROUP,
  IMPERSONATION_APP_EVENTS,
  IMPERSONATION_USER_EVENTS,
} from "../constants";
import type { ImpersonationSession } from "../types";

/**
 * Handles postMessage communication between the admin app and the worker app iframe
 * for the impersonation auth flow.
 *
 * The worker app iframe sends an "impersonation:ready" message when its React app
 * has mounted and is ready to receive the token. This hook responds by sending the
 * admin's Cognito ID token to the iframe.
 *
 * Each hook instance only responds to messages from its own session's origin,
 * preventing cross-talk between multiple concurrent impersonation sessions.
 */
interface UseImpersonationMessagingOptions {
  employeeId: string | undefined;
  session: ImpersonationSession;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onSessionEnd: () => void;
  onCleanupComplete: () => void;
  onExpiresAtUpdate: (expiresAt: number) => void;
}

export function useImpersonationMessaging({
  employeeId,
  session,
  iframeRef,
  onSessionEnd,
  onCleanupComplete,
  onExpiresAtUpdate,
}: UseImpersonationMessagingOptions) {
  const sessionOrigin = useMemo(() => new URL(session.url).origin, [session.url]);
  const isAcknowledged = useRef(false);
  const [error, setError] = useState<string>();

  const sendTokenToIframe = useCallback(async () => {
    if (isAcknowledged.current) {
      return;
    }

    if (!iframeRef.current?.contentWindow) {
      return;
    }

    try {
      const idToken = await getCognitoIdToken();
      if (!idToken) {
        const message = "Failed to get admin authentication token. Please sign in again.";
        setError(message);
        logError(IMPERSONATION_APP_EVENTS.TOKEN_SEND_FAILURE, {
          error: new Error("Failed to get Cognito ID token for impersonation"),
          metadata: {
            employeeId,
            targetId: session.targetId,
            sessionType: session.sessionType,
            sessionId: session.id,
          },
        });
        return;
      }

      const groups = await getCognitoGroups();
      if (!groups.includes(COGNITO_IMPERSONATION_GROUP)) {
        const message =
          "You are not in the impersonation group. Ask your manager to remove and re-add the impersonation role, then sign out and back in to sync your permissions.";
        setError(message);
        logError(IMPERSONATION_APP_EVENTS.MISSING_COGNITO_GROUP, {
          error: new Error("Admin is not in the Cognito impersonation group"),
          metadata: {
            employeeId,
            targetId: session.targetId,
            targetName: session.targetName,
            sessionType: session.sessionType,
            sessionId: session.id,
            cognitoGroups: groups,
          },
        });
        return;
      }

      iframeRef.current.contentWindow.postMessage(
        { type: "employeeIdToken", token: idToken },
        sessionOrigin
      );
    } catch (error_) {
      const message =
        error_ instanceof Error
          ? error_.message
          : "An unexpected error occurred during impersonation.";
      setError(message);
      logError(IMPERSONATION_APP_EVENTS.TOKEN_SEND_FAILURE, {
        error: error_,
        metadata: {
          employeeId,
          targetId: session.targetId,
          sessionType: session.sessionType,
          sessionId: session.id,
        },
      });
    }
  }, [
    employeeId,
    iframeRef,
    sessionOrigin,
    session.targetId,
    session.targetName,
    session.sessionType,
    session.id,
  ]);

  const resetTokenState = useCallback(() => {
    isAcknowledged.current = false;
    setError(undefined);
  }, []);

  // Listen for messages from this session's worker app iframe only
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Only handle messages from this session's specific iframe window + origin
      if (event.origin !== sessionOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data as { type?: string; error?: string; expiresAt?: number } | undefined;

      if (!data?.type) {
        return;
      }

      // Iframe is ready — send the token
      if (data.type === "impersonation:ready") {
        void sendTokenToIframe();
        return;
      }

      if (data.type === "impersonation:success") {
        isAcknowledged.current = true;
        if (data.expiresAt) {
          onExpiresAtUpdate(data.expiresAt);
        }

        logEvent(IMPERSONATION_USER_EVENTS.SESSION_OPENED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: session.id,
          status: "authenticated",
        });
      }

      if (data.type === "impersonation:error") {
        isAcknowledged.current = true;
        setError(data.error ?? "Impersonation authentication failed.");
        logError(IMPERSONATION_APP_EVENTS.AUTH_FAILURE, {
          error: new Error(data.error ?? "Impersonation authentication failed"),
          metadata: {
            employeeId,
            targetId: session.targetId,
            sessionType: session.sessionType,
            sessionId: session.id,
          },
        });
      }

      if (data.type === "impersonation:sessionExpired") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_EXPIRED, {
          employeeId,
          targetId: session.targetId,
          sessionType: session.sessionType,
          sessionId: session.id,
        });
        return;
      }

      if (data.type === "impersonation:sessionEnded") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_CLOSED, {
          employeeId,
          targetId: session.targetId,
          sessionType: session.sessionType,
          sessionId: session.id,
        });
        onSessionEnd();
      }

      if (data.type === "impersonation:cleanupComplete") {
        onCleanupComplete();
      }
    }

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- iframeRef is a stable ref, reading .current at event time is correct
  }, [
    employeeId,
    sessionOrigin,
    session.targetId,
    session.targetName,
    session.sessionType,
    session.id,
    sendTokenToIframe,
    onSessionEnd,
    onCleanupComplete,
    onExpiresAtUpdate,
  ]);

  return { error, resetTokenState };
}
